import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Header from '@/components/base/Header';
import Footer from '@/components/base/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { animeList, featuredAnime, latestUpdates } from '@/mocks/anime';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => {
    return animeList.find((a) => a.slug === slug);
  }, [slug]);

  const relatedAnime = useMemo(() => {
    if (!article) return [];
    const sameCategory = animeList.filter(
      (a) =>
        a.id !== article.id &&
        a.categories.some((c) => article.categories.includes(c))
    );
    return sameCategory.slice(0, 6);
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Header />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 pt-24 pb-16 text-center">
          <div className="w-20 h-20 mx-auto bg-background-100 rounded-full flex items-center justify-center mb-4">
            <i className="ri-emotion-sad-line text-foreground-400 text-3xl"></i>
          </div>
          <h1 className="font-heading text-2xl text-foreground-950 mb-2">文章不存在</h1>
          <p className="text-sm text-foreground-500 mb-6">该文章可能已被移除或链接无效。</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: `${article.year}-01-01`,
    dateModified: article.updateTime,
    image: article.cover,
    publisher: {
      '@type': 'Organization',
      name: '51动漫网',
      url: 'https://www.51dmw.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.51dmw.com/article/${article.slug}`,
    },
  };

  const recommendations = [...featuredAnime, ...latestUpdates]
    .filter((a) => a.id !== article.id)
    .slice(0, 4);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <ScrollToTop />
      <Header />
      <Breadcrumb />
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 pb-16">
        <article className="pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <span className="text-sm font-medium text-foreground-800">{article.rating}</span>
                </div>
                <span className="text-foreground-300">|</span>
                <span className="text-sm text-foreground-500">作者：{article.author}</span>
                <span className="text-foreground-300">|</span>
                <span className="text-sm text-foreground-500">{article.region}</span>
                <span className="text-foreground-300">|</span>
                <span className="text-sm text-foreground-500">{article.year}年</span>
                <span className="text-foreground-300">|</span>
                <span className={`text-sm font-medium whitespace-nowrap ${
                  article.status === '连载中' ? 'text-primary-600' : 'text-foreground-500'
                }`}>
                  {article.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {article.categories.map((cat) => {
                  const catObj = animeList.find(() => true)?.categories.includes(cat)
                    ? { slug: cat === '日漫' ? 'riman' : cat === '国漫' ? 'guoman' : cat === '韩漫' ? 'hanman' : cat === '少年热血' ? 'shaonian' : cat === '玄幻奇幻' ? 'xuanhuan' : cat === '恋爱校园' ? 'aiqing' : cat === '搞笑日常' ? 'xiju' : cat === '悬疑推理' ? 'xuanyi' : cat === '科幻未来' ? 'kehuan' : 'zhanji' }
                    : { slug: 'riman' };
                  return (
                    <Link
                      key={cat}
                      to={`/category/${catObj.slug}`}
                      className="text-xs px-3 py-1 bg-accent-100 text-accent-700 rounded-full hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat}
                    </Link>
                  );
                })}
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 bg-background-100 text-foreground-500 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100/50 rounded-2xl p-6 mb-8">
                <h2 className="font-heading text-lg text-foreground-950 mb-3">作品简介</h2>
                <div className="article-content">
                  <p>{article.description}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 text-center">
                  <p className="text-2xl font-heading text-primary-500">{article.totalChapters}</p>
                  <p className="text-xs text-foreground-500 mt-1">总章节</p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 text-center">
                  <p className="text-2xl font-heading text-accent-500">{(article.views / 10000).toFixed(0)}万</p>
                  <p className="text-xs text-foreground-500 mt-1">总阅读</p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 text-center">
                  <p className="text-2xl font-heading text-secondary-500">{article.rating}</p>
                  <p className="text-xs text-foreground-500 mt-1">评分</p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 text-center">
                  <p className="text-2xl font-heading text-foreground-700">{article.status === '连载中' ? 'ing' : '✓'}</p>
                  <p className="text-xs text-foreground-500 mt-1">状态</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-6 text-center">
                <p className="text-background-50 font-heading text-lg mb-2">
                  更新至 {article.updateChapter}
                </p>
                <p className="text-background-200/80 text-sm mb-4">
                  最近更新：{article.updateTime}
                </p>
                <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-50 text-primary-600 rounded-full text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-play-circle-line"></i>
                  立即阅读
                </button>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="rounded-2xl overflow-hidden aspect-[3/4] mb-6">
                  <img
                    src={article.cover}
                    alt={article.title}
                    title={`${article.title} - 51动漫网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="bg-background-100/50 rounded-2xl p-5 mb-6">
                  <h3 className="font-heading text-base text-foreground-950 mb-4">基本信息</h3>
                  <div className="space-y-2.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">作者</span>
                      <span className="text-foreground-800">{article.author}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">地区</span>
                      <span className="text-foreground-800">{article.region}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">年份</span>
                      <span className="text-foreground-800">{article.year}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">状态</span>
                      <span className={`font-medium ${article.status === '连载中' ? 'text-primary-600' : 'text-foreground-600'}`}>
                        {article.status}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">总章节</span>
                      <span className="text-foreground-800">{article.totalChapters}话</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">评分</span>
                      <span className="text-accent-600 font-medium">{article.rating}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-heading text-base text-foreground-950 mb-4">推荐阅读</h3>
                  <div className="space-y-3">
                    {recommendations.map((rec) => (
                      <Link
                        key={rec.id}
                        to={`/article/${rec.slug}`}
                        className="flex items-center gap-3 group cursor-pointer"
                      >
                        <div className="w-12 h-16 rounded-lg overflow-hidden shrink-0">
                          <img
                            src={rec.cover}
                            alt={rec.title}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">
                            {rec.title}
                          </p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                            <span className="text-xs text-foreground-500">{rec.rating}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {relatedAnime.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/70">
              <h2 className="font-heading text-xl text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                {relatedAnime.map((anime) => (
                  <Link
                    key={anime.id}
                    to={`/article/${anime.slug}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-xl aspect-[3/4] mb-3">
                      <img
                        src={anime.cover}
                        alt={anime.title}
                        title={`${anime.title} - 51动漫网`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 right-2 flex items-center gap-1 bg-background-950/70 backdrop-blur-sm rounded-full px-2 py-0.5">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                        <span className="text-xs text-background-50 font-medium">{anime.rating}</span>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {anime.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}